import { ButtonBase } from '@mui/material';
import { useApp } from 'contexts/app';
import { useState } from 'react';
import { Box, Divider, IconButton, Paper, Stack, Text, Tooltip, ViewMore, styled } from 'shell/components';
import { getTooltipComponent } from 'utils/helper';

const FeedbackRenderer = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    if (feedback.is_failed) {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            General Feedback
                        </Text>
                        <Tooltip
                            content={getTooltipComponent(
                                'General Feedback',
                                'This section provides a broad overview, highlighting key strengths and areas for improvement across your work.'
                            )}
                            type={'info'}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            We were unable to generate feedback for this assignment. Please click the regenerate button
                            above to try again.
                        </Text>
                    </Stack>
                </Stack>
            </>
        );
    }

    if (typeof feedback.feedback === 'string') {
        return (
            <>
                <BackgroundStack>
                    <Text variant={'p1'} weight={'semibold'}>
                        Assignment Feedback
                    </Text>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <CustomText rich>{feedback.feedback}</CustomText>
                </Stack>
            </>
        );
    }

    if (feedback.feedback.feedback_type === 'rubric') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        Assignment Feedback
                    </Text>
                </BackgroundStack>
                <Stack
                    sx={(theme) => ({
                        flex: 1,
                        p: 3,
                        background: theme.palette.ensightful.neutral[300],
                    })}
                    spacing={2.5}
                >
                    {feedback.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .map((item) => (
                            <CriterionFeedback
                                key={item.id}
                                feedback={item}
                                selectedFeedbackId={selectedFeedbackId}
                                setSelectedFeedbackId={setSelectedFeedbackId}
                                onClickThumbsUp={onClickThumbsUp}
                                onClickThumbsDown={onClickThumbsDown}
                            />
                        ))}
                </Stack>
            </>
        );
    }

    if (feedback.feedback.feedback_type === 'system') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            General Feedback
                        </Text>
                        <Tooltip
                            content={getTooltipComponent(
                                'General Feedback',
                                'This section provides a broad overview, highlighting key strengths and areas for improvement across your work.'
                            )}
                            type={'info'}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <>
                        {!feedback.feedback.commentary ? (
                            <Text sx={{ my: 4 }} align={'center'} color={'light'}>
                                This feedback was deleted by your instructor(s).
                            </Text>
                        ) : (
                            <CustomText rich>{feedback.feedback.commentary}</CustomText>
                        )}
                    </>
                </Stack>
                {feedback.feedback.feedback_type !== 'custom' && (
                    <>
                        <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    Detailed Feedback
                                </Text>
                                <Tooltip
                                    content={getTooltipComponent(
                                        'Detailed Feedback',
                                        'Here, specific feedback is provided with highlighted examples. You can use these examples to help them identify other areas where you can improve.'
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                        </BackgroundStack>
                        <Stack
                            sx={(theme) => ({
                                position: 'relative',
                                flex: 1,
                                p: 3,
                                background: theme.palette.ensightful.neutral[300],
                            })}
                            spacing={2.5}
                        >
                            {feedback.feedback.improvement_suggestions.every(({ active }) => !active) ? (
                                <Text
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '100%',
                                    }}
                                    align={'center'}
                                    color={'light'}
                                >
                                    This feedback was deleted by your instructor(s).
                                </Text>
                            ) : (
                                feedback.feedback.improvement_suggestions
                                    .filter(({ active }) => active)
                                    .map((item) => (
                                        <DetailedFeedback
                                            key={item.id}
                                            feedback={item}
                                            selectedFeedbackId={selectedFeedbackId}
                                            setSelectedFeedbackId={setSelectedFeedbackId}
                                            onClickThumbsUp={onClickThumbsUp}
                                            onClickThumbsDown={onClickThumbsDown}
                                        />
                                    ))
                            )}
                        </Stack>
                    </>
                )}
            </>
        );
    }

    return null;
};

const CriterionFeedback = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    const { currentAssignment } = useApp();

    const [isHovered, setIsHovered] = useState(false);

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack spacing={2}>
                    <Text variant={'sh2'} weight={'semibold'}>
                        {currentAssignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                    </Text>
                    <Divider />
                    <Stack spacing={3}>
                        <Stack spacing={2}>
                            <Text variant={'p1'}>What you did well</Text>
                            {feedback.positive.active ? (
                                <ViewMore rich text={feedback.positive.text} />
                            ) : (
                                <Text color={'light'}>This feedback was deleted.</Text>
                            )}
                        </Stack>
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '4px',
                                mx: -1,
                                p: 2,
                                background: theme.palette.ensightful.warning[200],
                            })}
                            spacing={2}
                        >
                            <Text variant={'p1'}>How you can improve</Text>
                            {feedback.negative.active ? (
                                <ViewMore rich text={feedback.negative.text} />
                            ) : (
                                <Text color={'light'}>This feedback was deleted.</Text>
                            )}
                        </Stack>
                    </Stack>
                    {onClickThumbsUp && onClickThumbsDown && (
                        <Stack sx={{ alignSelf: 'flex-end' }} direction={'row'} spacing={2}>
                            <AgreeIconButton
                                aria-label={'agree'}
                                active={feedback.agree}
                                kind={'thumbsUp'}
                                size={'small'}
                                tooltip={{ component: 'This was helpful' }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsUp(feedback);
                                }}
                            />
                            <DisagreeIconButton
                                aria-label={'disagree'}
                                active={feedback.disagree}
                                kind={'thumbsDown'}
                                size={'small'}
                                tooltip={{ component: 'This was unhelpful or incorrect' }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsDown(feedback);
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const DetailedFeedback = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack spacing={2}>
                    <Text align={'left'} rich>
                        {feedback.text}
                    </Text>
                    {onClickThumbsUp && onClickThumbsDown && (
                        <Stack sx={{ alignSelf: 'flex-end' }} direction={'row'} spacing={2}>
                            <AgreeIconButton
                                aria-label={'agree'}
                                active={feedback.agree}
                                kind={'thumbsUp'}
                                size={'small'}
                                tooltip={{ component: 'This was helpful' }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsUp(feedback);
                                }}
                            />
                            <DisagreeIconButton
                                aria-label={'disagree'}
                                active={feedback.disagree}
                                kind={'thumbsDown'}
                                size={'small'}
                                tooltip={{ component: 'This was unhelpful or incorrect' }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsDown(feedback);
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const CustomText = styled(Text)(() => {
    return {
        '& ul li': {
            listStyleType: '"- "',
        },
    };
});

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `3px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `3px solid ${theme.palette.ensightful.shades[0]}`,
              }),
    };
});

const AgreeIconButton = styled(IconButton)(({ theme, active }) => {
    return {
        ...(active && {
            background: theme.palette.ensightful.success[100],
            color: theme.palette.ensightful.success[700],
            '&:hover': {
                background: theme.palette.ensightful.success[100],
                color: theme.palette.ensightful.success[700],
            },
        }),
    };
});

const DisagreeIconButton = styled(IconButton)(({ theme, active }) => {
    return {
        ...(active && {
            background: theme.palette.ensightful.error[100],
            color: theme.palette.ensightful.error[700],
            '&:hover': {
                background: theme.palette.ensightful.error[100],
                color: theme.palette.ensightful.error[700],
            },
        }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-3px',
        height: 'calc(100% - 12px)',
        width: '3px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

export default FeedbackRenderer;
